import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { Select as Multiselect } from 'chakra-react-select';
import {
  getOrganisations,
  getOrgUsers,
} from '../../../stores/slices/organisations/organisationsActions';
import { addCoachUsers } from '../../../stores/slices/userAccess/userAccessActions';

const UserAccessModal = ({ isOpen, onOpen, onClose }) => {
  const { organisations, isLoading } = useSelector(
    state => state.organisations
  );
  const [orgList, setOrgList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const schema = yup
    .object({
      organisation: yup
        .object()
        .nullable()
        .required('Organisation is required'),
      employee: yup
        .array()
        .min(1, 'Please select at least 1')
        .required('Employee is required'),
    })
    .required();
  const dispatch = useDispatch();
  const toast = useToast();
  useEffect(() => {
    dispatch(getOrganisations());
  }, []);
  useEffect(() => {
    setOrgList(
      organisations.map(data => {
        return {
          label: data.name,
          value: data.id,
        };
      })
    );
  }, [organisations]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      organisation: null,
    },
  });

  const watchOrg = watch('organisation');
  const getUsers = async () => {
    const res = await dispatch(getOrgUsers({ orgId: watchOrg.value }));
    setUserList(
      res.payload.data.map(data => {
        return {
          label: data.full_name,
          value: data.id,
        };
      })
    );

    setFetchingUsers(false);
  };
  useEffect(() => {
    if (watchOrg !== null) {
      setFetchingUsers(true);
      getUsers();
    }
  }, [watchOrg]);

  const onSubmit = async data => {
    const res = await dispatch(
      addCoachUsers({
        users: data.employee.map(emp => emp.value),
      })
    );
    if (res.type === 'access/addCoachUsers/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      reset();
      onClose();
      toast({
        description: 'Request has been sent',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };
  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Access Request</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={'2'}>
              <Controller
                control={control}
                name="organisation"
                render={({
                  field: { onChange, onBlur, value, name, ref },
                  fieldState: { error },
                }) => (
                  <FormControl isInvalid={!!error} id="organisation">
                    <FormLabel>Organisation</FormLabel>
                    <Multiselect
                      isMulti={false}
                      name={name}
                      ref={ref}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      options={orgList}
                      placeholder="Select one"
                      isClearable
                      closeMenuOnSelect
                      className="chakra-react-select"
                      classNamePrefix="chakra-react-select"
                      selectedOptionStyle="check"
                      isLoading={isLoading}
                      chakraStyles={{
                        dropdownIndicator: provided => ({
                          ...provided,
                          bg: 'transparent',
                          px: 2,
                          cursor: 'inherit',
                        }),
                        indicatorSeparator: provided => ({
                          ...provided,
                          display: 'none',
                        }),
                      }}
                    />

                    <FormErrorMessage>
                      {error && error.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="employee"
                render={({
                  field: { onChange, onBlur, value, name, ref },
                  fieldState: { error },
                }) => (
                  <FormControl isInvalid={!!error} id="employee">
                    <FormLabel>Employee</FormLabel>
                    <Multiselect
                      isMulti
                      name={name}
                      ref={ref}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      options={userList}
                      placeholder="Select employee"
                      isClearable
                      closeMenuOnSelect
                      className="chakra-react-select"
                      classNamePrefix="chakra-react-select"
                      selectedOptionStyle="check"
                      isLoading={fetchingUsers}
                      chakraStyles={{
                        dropdownIndicator: provided => ({
                          ...provided,
                          bg: 'transparent',
                          px: 2,
                          cursor: 'inherit',
                        }),
                        indicatorSeparator: provided => ({
                          ...provided,
                          display: 'none',
                        }),
                      }}
                    />

                    <FormErrorMessage>
                      {error && error.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} type="submit" isLoading={isSubmitting}>
              Save
            </Button>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
              variant="outline"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UserAccessModal;
